import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '@features/auth/hooks/useAuth';
import { LoadingSpinner } from '@shared/components/feedback/LoadingSpinner';

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react';

// Layouts
const TeacherLayout = lazy(
  () => import('@shared/components/layout/TeacherLayout')
);
const StudentLayout = lazy(
  () => import('@shared/components/layout/StudentLayout')
);
const AdminLayout = lazy(
  () => import('@shared/components/layout/AdminLayout')
);

// Public pages
const LandingPage = lazy(() => import('@features/auth/components/LandingPage'));
const LoginPage = lazy(() => import('@features/auth/components/LoginPage'));
const SignupPage = lazy(() => import('@features/auth/components/SignupPage'));
const ForgotPasswordPage = lazy(() => import('@features/auth/components/ForgotPasswordPage'));
const JoinClassPage = lazy(() => import('@features/classes/components/JoinClassPage'));

// Legal pages
const TermsOfServicePage = lazy(() => import('@/pages/legal/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/PrivacyPolicyPage'));

// Help page
const HelpCenterPage = lazy(() => import('@/pages/help/HelpCenterPage'));

// Subscription pages
const PricingPage = lazy(
  () => import('@features/subscription/components/PricingPage')
);
const CheckoutSuccess = lazy(
  () => import('@features/subscription/components/CheckoutSuccess')
);
const CheckoutCancel = lazy(
  () => import('@features/subscription/components/CheckoutCancel')
);
const SubscriptionManagement = lazy(
  () => import('@features/subscription/components/SubscriptionManagement')
);
const SubscriptionDashboard = lazy(
  () => import('@features/subscription/components/dashboard/SubscriptionDashboard')
);
const InvoiceHistoryPage = lazy(
  () => import('@features/subscription/components/invoices/InvoiceHistoryPage')
);

// Settings pages
const SettingsPage = lazy(
  () => import('@features/settings/components/SettingsPage')
);
const TeacherSettingsPage = lazy(
  () => import('@features/settings/components/TeacherSettingsPage')
);
const StudentSettingsPage = lazy(
  () => import('@features/settings/components/StudentSettingsPage')
);

// Admin pages
const AdminDashboard = lazy(
  () => import('@features/admin/components/AdminDashboard')
);
const PilotList = lazy(
  () => import('@features/admin/components/PilotManagement/PilotList')
);
const AdminUsersPage = lazy(
  () => import('@features/admin/components/AdminUsersPage')
);
const AdminSubscriptionsPage = lazy(
  () => import('@features/admin/components/AdminSubscriptionsPage')
);

// Teacher pages
const TeacherDashboard = lazy(
  () => import('@features/classes/components/TeacherDashboard')
);
const ClassListPage = lazy(
  () => import('@features/classes/components/ClassListPage')
);
const ClassDetailPage = lazy(
  () => import('@features/classes/components/ClassDetailPage')
);

// Question Bank pages
const QuestionBankListPage = lazy(
  () => import('@features/questions/components/QuestionBankListPage')
);
const QuestionBankDetailPage = lazy(
  () => import('@features/questions/components/QuestionBankDetailPage')
);

// Assessment pages
const AssessmentListPage = lazy(
  () => import('@features/assessments/components/AssessmentListPage')
);

// Rubric pages
const RubricListPage = lazy(
  () => import('@features/assessments/components/rubrics/RubricListPage')
);

// Grading pages
const GradingQueuePage = lazy(
  () => import('@features/grading/components/GradingQueuePage')
);
const SubmissionGradingPage = lazy(
  () => import('@features/grading/components/SubmissionGradingPage')
);

// Analytics pages
const AnalyticsLayout = lazy(
  () => import('@features/analytics/components/AnalyticsLayout')
);
const AnalyticsHub = lazy(
  () => import('@features/analytics/components/AnalyticsHub')
);
const ClassAnalyticsList = lazy(
  () => import('@features/analytics/components/ClassAnalyticsList')
);
const StudentProgressList = lazy(
  () => import('@features/analytics/components/StudentProgressList')
);
const QuestionAnalyticsPage = lazy(
  () => import('@features/analytics/components/QuestionAnalysis/QuestionAnalyticsPage')
);
const ClassGroupingView = lazy(
  () => import('@features/analytics/components/ClassGrouping/ClassGroupingView')
);
const StudentProgressPage = lazy(
  () => import('@features/analytics/components/StudentProgress/StudentProgressPage')
);

// Reports & Gradebook pages
const GradebookPage = lazy(
  () => import('@features/reports/components/GradebookPage')
);

// Student pages
const StudentDashboard = lazy(
  () => import('@features/student-portal/components/StudentDashboard')
);
const StudentClassView = lazy(
  () => import('@features/student-portal/components/StudentClassView')
);
const StudentAllResultsPage = lazy(
  () => import('@features/student-portal/components/StudentAllResultsPage')
);
const StudentAllQuizzesPage = lazy(
  () => import('@features/student-portal/components/StudentAllQuizzesPage')
);
const AssessmentTakingPage = lazy(
  () => import('@features/assessment-taking/components/AssessmentTakingPage')
);
const ResultsPage = lazy(
  () => import('@features/results/components/ResultsPage')
);

// Route guard component
function RequireAuth({
  children,
  role,
}: {
  children: React.ReactNode;
  role?: 'teacher' | 'student' | 'admin';
}) {
  const { user, loading, isAuthenticated, isAdmin } = useAuth();

  // Debug logging in development
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[RequireAuth]', {
      loading,
      isAuthenticated,
      userRole: user?.role,
      requiredRole: role,
    });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-primary-600 dark:bg-gray-900 dark:text-primary-400">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[RequireAuth] Not authenticated, redirecting to /login');
    }
    return <Navigate to="/login" replace />;
  }

  const hasRole = role === 'admin'
    ? (user?.role === 'admin' || isAdmin)
    : user?.role === role;

  if (role && !hasRole) {
    // Redirect to appropriate dashboard based on actual user role
    let redirectPath = '/';
    if (user?.role === 'teacher') {
      redirectPath = '/teacher';
    } else if (user?.role === 'student') {
      redirectPath = '/student';
    } else if (user?.role === 'admin') {
      redirectPath = '/admin';
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[RequireAuth] Role mismatch, redirecting to ${redirectPath}`);
    }
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}

// Loading fallback component with dark mode support to prevent flash
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 text-primary-600 dark:bg-gray-900 dark:text-primary-400">
      <LoadingSpinner size="lg" />
    </div>
  );
}

// Wrapper for ClassGroupingView to get classId from route params
function ClassGroupingWrapper() {
  const { classId } = useParams<{ classId: string }>();
  return <ClassGroupingView classId={classId || ''} />;
}

export function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/join" element={<JoinClassPage />} />
        <Route path="/join/:inviteCode" element={<JoinClassPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/help" element={<HelpCenterPage />} />

        {/* Checkout routes */}
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel" element={<CheckoutCancel />} />

        {/* Teacher routes - wrapped in TeacherLayout */}
        <Route
          path="/teacher"
          element={
            <RequireAuth role="teacher">
              <TeacherLayout />
            </RequireAuth>
          }
        >
          <Route index element={<TeacherDashboard />} />

          {/* Settings routes nested in TeacherLayout */}
          <Route path="settings/account" element={<SettingsPage />} />
          <Route path="settings/subscription" element={<SubscriptionDashboard />} />
          <Route path="settings/billing" element={<SubscriptionManagement />} />
          <Route path="settings/invoices" element={<InvoiceHistoryPage />} />
          <Route path="classes" element={<ClassListPage />} />
          <Route path="classes/:classId" element={<ClassDetailPage />} />
          <Route path="assessments" element={<AssessmentListPage />} />
          {/* Redirect create route to dashboard where the modal is available */}
          <Route
            path="assessments/create"
            element={<Navigate to="/teacher" replace />}
          />
          <Route path="grading" element={<GradingQueuePage />} />
          <Route
            path="grading/:submissionId"
            element={<SubmissionGradingPage />}
          />
          {/* Analytics routes with nested sub-navigation */}
          <Route path="analytics" element={<AnalyticsLayout />}>
            <Route index element={<AnalyticsHub />} />
            <Route path="classes" element={<ClassAnalyticsList />} />
            <Route path="class/:classId" element={<ClassGroupingWrapper />} />
            <Route path="students" element={<StudentProgressList />} />
            <Route path="student/:studentId" element={<StudentProgressPage />} />
            <Route path="questions" element={<QuestionAnalyticsPage />} />
            <Route path="question/:questionId" element={<QuestionAnalyticsPage />} />
          </Route>
          <Route path="questions" element={<QuestionBankListPage />} />
          <Route path="questions/:bankId" element={<QuestionBankDetailPage />} />
          <Route path="rubrics" element={<RubricListPage />} />
          {/* Gradebook routes */}
          <Route path="gradebook" element={<GradebookPage />} />
          <Route path="gradebook/:classId" element={<GradebookPage />} />
          <Route path="settings" element={<TeacherSettingsPage />} />
        </Route>

        {/* Student routes - wrapped in StudentLayout */}
        <Route
          path="/student"
          element={
            <RequireAuth role="student">
              <StudentLayout />
            </RequireAuth>
          }
        >
          <Route index element={<StudentDashboard />} />
          <Route path="class/:classId" element={<StudentClassView />} />
          <Route
            path="assessment/:assessmentId"
            element={<AssessmentTakingPage />}
          />
          <Route path="quizzes" element={<StudentAllQuizzesPage />} />
          <Route path="results" element={<StudentAllResultsPage />} />
          <Route
            path="results/:submissionId"
            element={<ResultsPage />}
          />
          <Route path="settings" element={<StudentSettingsPage />} />
        </Route>

        {/* Admin routes - wrapped in AdminLayout */}
        <Route
          path="/admin"
          element={
            <RequireAuth role="admin">
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="pilots" element={<PilotList />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route
            path="subscriptions"
            element={<AdminSubscriptionsPage />}
          />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
